import React from 'react';
import styled from 'styled-components/macro';
import { Heading, Text, NumberValue } from '../../typography';
import { SPACINGS, Highlight } from '../../utils';

type PPCardStyledProps = {
  isSelected?: boolean;
  isHoverActive?: boolean;
};
const PortfolioProposalCardStyled = styled.div<PPCardStyledProps>`
  display: flex;
  border: 2px solid
    ${(p) =>
      p.isSelected ? p.theme.colors.primary : p.theme.colors.secondary90w};
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: ${(p) => (p.isHoverActive ? p.theme.boxShadows.base : 'none')};
  }

  .image {
    display: flex;
    align-items: center;
    overflow: hidden;
    width: 100px;
    min-width: 100px;
    border-right: 1px solid ${(p) => p.theme.colors.secondary90w};
  }

  .title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: ${SPACINGS[6]};
    border-bottom: 1px solid ${(p) => p.theme.colors.secondary90w};
  }

  .title-actions {
    display: flex;
    align-items: center;
  }

  .title-actions > * {
    margin-right: ${SPACINGS[4]};
  }

  .title-actions > *:last-child {
    margin-right: 0;
  }

  .body {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .columns {
    display: flex;
    width: 100%;
  }

  .col-main {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: ${SPACINGS[6]};
    border-right: 1px solid ${(p) => p.theme.colors.secondary90w};
  }

  .col-secondary {
    padding: ${SPACINGS[6]};
  }

  .secondary-values {
    display: flex;
    justify-content: space-between;

    & > * {
      white-space: nowrap;
    }
  }

  .main-values {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    margin-bottom: ${SPACINGS[4]};
  }

  .main-value {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1 1 0;
    max-width: 150px;
    padding: ${SPACINGS[4]};
    border: 2px solid ${(p) => p.theme.colors.primary};
    border-radius: ${(p) => p.theme.dimensions.borderRadius};

    & > * {
      text-align: center;
    }

    &.min {
      border-color: ${(p) => p.theme.colors.minimum};
    }

    &.mean {
      padding-bottom: ${SPACINGS[6]};
      margin: 0 ${SPACINGS[2]};
    }

    &.max {
      border-color: ${(p) => p.theme.colors.maximum};
    }
  }
`;

type Value = {
  label: string;
  value: number;
  type: 'percentage' | 'day' | 'year';
};

type PortfolioProposalCardProps = {
  title: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  image?: React.ReactNode;
  isSelected?: boolean;
  isHoverActive?: boolean;
  onClick?: () => void;
  style?: React.CSSProperties;

  main: {
    label: string | React.ReactNode;
    values: Value[];
    disclaimer?: string;
  };

  secondary: {
    label: string | React.ReactNode;
    value: string | React.ReactNode;
    values?: Value[];
  };
};

function PortfolioProposalCard(props: PortfolioProposalCardProps) {
  const {
    title,
    subtitle,
    actions,
    image,
    isSelected,
    isHoverActive,
    onClick,
    main,
    secondary,
    style,
  } = props;

  const max = main.values[0];
  const mean = main.values[1];
  const min = main.values[2];

  function handleOnClick() {
    if (onClick) onClick();
  }

  return (
    <PortfolioProposalCardStyled
      isSelected={isSelected}
      isHoverActive={isHoverActive}
      onClick={handleOnClick}
      style={style}
    >
      <div className="image">{image}</div>
      <div className="body">
        <div className="title">
          <div>
            <Heading margin="none" type={4}>
              {title}
            </Heading>
            {subtitle}
          </div>
          <div className="title-actions">{actions}</div>
        </div>
        <div className="columns">
          <div className="col-main">
            <Text type="label" align="center">
              {main.label}
            </Text>
            <div className="main-values">
              <div className="main-value max">
                <NumberValue value={max.value} type={max.type} />
                <Text type="label" size="sm">
                  {max.label}
                </Text>
              </div>
              <div className="main-value mean">
                <Highlight>
                  <NumberValue value={mean.value} type={mean.type} size="xxl" />
                </Highlight>
                <Text type="label" size="sm">
                  {mean.label}
                </Text>
              </div>
              <div className="main-value min">
                <NumberValue value={min.value} type={min.type} />
                <Text type="label" size="sm">
                  {min.label}
                </Text>
              </div>
            </div>
            {main.disclaimer && (
              <Text type="label" size="xs">
                {main.disclaimer}
              </Text>
            )}
          </div>
          <div className="col-secondary">
            <Text type="label">{secondary.label}</Text>
            <div style={{ marginBottom: SPACINGS[4] }}>
              {typeof secondary.value === 'string' ? (
                <Text size="lg">{secondary.value}</Text>
              ) : (
                secondary.value
              )}
            </div>
            <div>
              {secondary.values?.map((v) => (
                <div key={v.label} className="secondary-values">
                  <Text
                    type="label"
                    size="sm"
                    style={{ marginRight: SPACINGS[6] }}
                  >
                    {v.label}
                  </Text>
                  <NumberValue value={v.value} type={v.type} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PortfolioProposalCardStyled>
  );
}

export default PortfolioProposalCard;
